import * as React from 'react';
import { styled } from '@linaria/react';
import { RotateCcw } from 'lucide-react';

import ColorModeContainer from '@/components/ColorModeContainer';
import SecondaryButton from '@/components/ButtonPlayful/ButtonSecondary';
import ToggleButtonGroupControl from '@/components/Controls/ToggleButtonGroupControl';

import ArcGraph from './ArcGraph';

interface Point {
  x: number;
  y: number;
}

const RENDERED_SIZE = 270;
const VIEWBOX_SIZE = 16;

const INITIAL_POINTS = [
  { x: 2, y: 2 },
  { x: 2, y: 8 },
  { x: 14, y: 8 },
  { x: 14, y: 2 },
];

type BonusControl = 'large-arc-flag' | 'sweep-flag' | 'radius';

interface Props {
  initialPoints?: Array<Point>;
  color?: string;
  bonusControls?: Array<BonusControl>;
}

function DraggableArcPoints({
  initialPoints = INITIAL_POINTS,
  color = 'var(--wham-primary-500)',
  bonusControls = [],
}: Props) {
  const [arcSize, setArcSize] = React.useState<'small' | 'large'>(
    'small'
  );
  const [points, setPoints] =
    React.useState<Array<Point>>(initialPoints);
  const [activePointIndex, setActivePointIndex] = React.useState<
    number | null
  >(null);

  const handleReset = React.useCallback(() => {
    setPoints(initialPoints);
    setArcSize('small');
  }, [initialPoints]);

  return (
    <Wrapper
      colorMode="dark"
      data-can-be-annotated={false}
      data-has-bonus-controls={bonusControls.length > 0}
    >
      {bonusControls.includes('large-arc-flag') && (
        <ToggleButtonGroupControl
          inline
          wrapperStyle={{
            padding: 0,
            justifyContent: 'space-between',
          }}
          id="large-arc-flag"
          label="Arc size"
          value={arcSize}
          setValue={(value) => {
            setArcSize(value as 'small' | 'large');
          }}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Large', value: 'large' },
          ]}
        />
      )}
      <StyledArcGraph
        arcSize={arcSize}
        rx={5}
        ry={5}
        renderedSize={RENDERED_SIZE}
        viewboxSize={VIEWBOX_SIZE}
        points={points}
        setPoints={setPoints}
        activePointIndex={activePointIndex}
        setActivePointIndex={setActivePointIndex}
        handleReset={handleReset}
        color={color}
      />
      <ResetButton Icon={RotateCcw} onClick={handleReset}>
        Reset
      </ResetButton>
    </Wrapper>
  );
}

const Wrapper = styled(ColorModeContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: fit-content;
  padding: 16px;
  margin-inline: auto;
  border-radius: 8px;
  margin-bottom: 64px;

  html[data-color-mode='dark'] & {
    outline: 1px solid var(--color-content-outline);
    outline-offset: -1px;
  }

  @media (max-width: 41rem) {
    width: calc(100% + 32px);
    margin-inline: -16px;
    grid-template-columns: 1fr;
    justify-items: center;
    grid-template-areas:
      'graph'
      'controls'
      'reset';
  }

  &[data-has-bonus-controls='true'] {
    grid-template-areas:
      'graph controls'
      'reset controls';

    @media (max-width: 41rem) {
      grid-template-areas:
        'graph'
        'controls'
        'reset';
    }
  }
`;

const StyledArcGraph = styled(ArcGraph)`
  grid-area: graph;
  align-self: center;
`;

const ResetButton = styled(SecondaryButton)`
  grid-area: reset;
  width: 100%;
`;

export default DraggableArcPoints;
